import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import NoChatImg from "assets/images/noChat.png";
import { CloseIcon, EyeOpenIcon, Tooltip } from "components";
import { useChatNotificationStore, useGlobalStore } from "controller";
import {
  useMarkAllNotificationsAsSeen,
  useUpdateChatNotificationStatus,
} from "controller/hooks/notification";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  chatThreadParentMessage,
  clearChatThreadParentMessage,
  setChatThreadParentMessage,
} from "shared/features/chat/store";
import { ThreadPanel } from "shared/features/chat/ThreadPanel";
import type { ChatResultItem } from "shared/types";
import { MessageListCard } from "./MessageListCard";
import { TaskChatContainer } from "./TaskChatContainer";
import { TicketChatContainer } from "./TicketChatContainer";

/**
 * Column widths for the drawer layout.
 *
 * The drawer paper must carry a *definite* width derived from these. The
 * message body below is rendered with MUI's `noWrap` (`white-space: nowrap`),
 * whose max-content width is the entire unwrapped message string. Blink clamps
 * that to the list's `flex: 0 0 364px`, but WebKit propagates it up the flex
 * tree — so any intrinsic sizing on the paper (shrink-to-fit or `max-content`)
 * blows the drawer out to the width of the longest message in Safari and
 * strands the list away from the right edge.
 */
const LIST_WIDTH = 364;
const CHAT_WIDTH = 588;
const THREAD_WIDTH = 588;
const COLUMN_GAP = 12;
const THREAD_GAP = 8;
export const CHAT_NOTIFICATION_EDGE_PADDING = 8;

export const getChatNotificationDrawerWidth = ({
  hasSelectedChat,
  isThreadOpen,
}: {
  hasSelectedChat: boolean;
  isThreadOpen: boolean;
}) => {
  // `pl` on the drawer body wrapper + `pr` on the container row.
  const edges = CHAT_NOTIFICATION_EDGE_PADDING * 2;

  if (!hasSelectedChat) {
    return edges + LIST_WIDTH;
  }

  // The list collapses to zero once a thread is open, but the row gap between
  // it and the chat column still occupies space.
  const listWidth = isThreadOpen ? 0 : LIST_WIDTH;
  const chatWidth = isThreadOpen
    ? CHAT_WIDTH + THREAD_GAP + THREAD_WIDTH
    : CHAT_WIDTH;

  return edges + listWidth + COLUMN_GAP + chatWidth;
};

export const MainChatNotificationContainer = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const resourceId = useGlobalStore((state) => state.resourceId);
  const selectedChat = useChatNotificationStore(
    (state) => state.selectedChatNotification,
  );
  const setAllChatNotificationAsSeen = useChatNotificationStore(
    (state) => state.setAllChatNotificationAsSeen,
  );
  const setSelectedChat = useChatNotificationStore(
    (state) => state.setSelectedChatNotification,
  );
  const allChatMessages = useChatNotificationStore(
    (state) => state.allChatNotifications,
  );
  const setChatNotificationAsSeen = useChatNotificationStore(
    (state) => state.setChatNotificationAsSeen,
  );
  const { mutateAsync: updateChatNotification } =
    useUpdateChatNotificationStatus();

  const {
    mutateAsync: markAllNotificationsAsSeen,
    isLoading: markAllAsReadLoading,
  } = useMarkAllNotificationsAsSeen();

  const threadParentMessage = useAtomValue(chatThreadParentMessage);
  const setThreadParent = useSetAtom(setChatThreadParentMessage);
  const clearThreadParent = useSetAtom(clearChatThreadParentMessage);
  const isThreadOpen = threadParentMessage !== null;

  useEffect(() => {
    if (!isOpen) {
      setSelectedChat(null);
      clearThreadParent();
    }
  }, [isOpen, clearThreadParent, setSelectedChat]);

  const onFullScreenButtonClick = ({
    serviceType,
    serviceId,
    projectId,
  }: {
    serviceType: "ticket" | "task";
    serviceId: string;
    projectId?: string;
  }) => {
    switch (serviceType) {
      case "ticket":
        clearThreadParent();
        onClose();
        navigate(`/app/service-desk/tickets/${serviceId}`);
        setSelectedChat(null);
        break;
      case "task":
        clearThreadParent();
        onClose();
        navigate(`/app/service-desk/projects/${projectId}/task/${serviceId}`);
        setSelectedChat(null);
        break;
      default:
        return;
    }
  };

  const handleMarkAsReadClick = async () => {
    try {
      await markAllNotificationsAsSeen({
        notificationType: "chat",
        resourceId: resourceId || "",
      });
      setAllChatNotificationAsSeen();
      toast.success("Marked all chat as read");
    } catch (_) {
      toast.error("Unable to mark all chat as read");
    }
  };

  const handleThreadClick = useCallback(
    (message: ChatResultItem) => {
      setThreadParent(message);
    },
    [setThreadParent],
  );

  const handleThreadClose = useCallback(() => {
    clearThreadParent();
  }, [clearThreadParent]);
  return (
    <Stack
      sx={{
        height: "100%",
        flexDirection: "row",
        gap: `${COLUMN_GAP}px`,
        background: "transparent",
        // boxShadow: " 0 8px 32px 0 rgba( 31, 38, 135, 0.37 )",
        // backdropFilter: "blur( 10.5px )",
        // WebkitBackdropFilter: "blur( 10.5px )",
        py: "8px",
        pr: `${CHAT_NOTIFICATION_EDGE_PADDING}px`,
      }}
    >
      <Stack
        sx={{
          height: "100%",
          flex: isThreadOpen ? "0 0 0px" : `0 0 ${LIST_WIDTH}px`,
          opacity: isThreadOpen ? 0 : 1,
          backgroundColor: theme.palette.neutral.main,
          borderRadius: "8px",
          overflow: "hidden",
          transition: "flex .3s ease, opacity .2s ease",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
          }}
        >
          <Box
            px="24px"
            paddingY="12px"
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            // borderBottom={`1px solid ${theme.palette.neutral["010"]}`}
          >
            <Typography
              component={"span"}
              variant={"h5"}
              sx={{ textTransform: "capitalize" }}
            >
              Messages
            </Typography>
            <Stack direction={"row"} gap={"4px"}>
              <Tooltip title="Mark all as read">
                <IconButton
                  onClick={() =>
                    !markAllAsReadLoading && handleMarkAsReadClick()
                  }
                >
                  <EyeOpenIcon htmlColor={theme.palette.lightBg.high} />
                </IconButton>
              </Tooltip>
              {!selectedChat && (
                <IconButton onClick={onClose}>
                  <CloseIcon htmlColor={theme.palette.lightBg.high} />
                </IconButton>
              )}
            </Stack>
          </Box>
        </Box>
        <Box sx={{ overflowY: "scroll", flexGrow: 1 }}>
          {!allChatMessages || allChatMessages.length <= 0 ? (
            <Stack
              flex={1}
              width={"100%"}
              justifyContent={"center"}
              alignItems={"center"}
              height={"100%"}
            >
              <img
                src={NoChatImg}
                alt=""
                width={"100%"}
                height={"100%"}
                style={{
                  objectFit: "contain",
                  maxHeight: "170px",
                  maxWidth: "170px",
                }}
              />
              <Typography variant="button-small">
                All clear! No new messages for you.
              </Typography>
            </Stack>
          ) : (
            allChatMessages?.map((item, index) => (
              <MessageListCard
                isSelected={selectedChat?.service_id === item.service_id}
                key={item.service_id}
                chatData={item}
                onClick={() => {
                  if (selectedChat?.service_id !== item.service_id) {
                    clearThreadParent();
                    updateChatNotification({
                      resourceId: resourceId || "",
                      notificationId: item.notification.notification_id,
                    });
                    setChatNotificationAsSeen(item.service_id);
                    setSelectedChat(item);
                  }
                }}
                onClose={onClose}
              />
            ))
          )}
        </Box>
      </Stack>
      {selectedChat &&
        (selectedChat.service_type === "ticket" ||
          selectedChat.service_type === "task") && (
          <Stack
            direction="row"
            sx={{
              flex: 1,
              height: "100%",
              gap: isThreadOpen ? `${THREAD_GAP}px` : "0px",
              transition: "gap .3s ease",
            }}
          >
            <Box
              sx={{
                flex: `0 0 ${CHAT_WIDTH}px`,
                height: "100%",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {selectedChat.service_type === "ticket" ? (
                <TicketChatContainer
                  selectedChat={selectedChat}
                  onClose={() => {
                    clearThreadParent();
                    setSelectedChat(null);
                  }}
                  onFullscreenClick={() => {
                    onFullScreenButtonClick({
                      serviceType: "ticket",
                      serviceId: selectedChat.service_id,
                    });
                  }}
                  onThreadClick={handleThreadClick}
                />
              ) : selectedChat.service_type === "task" ? (
                <TaskChatContainer
                  selectedChat={selectedChat}
                  onClose={() => {
                    clearThreadParent();
                    setSelectedChat(null);
                  }}
                  onFullscreenClick={() => {
                    onFullScreenButtonClick({
                      serviceType: "task",
                      serviceId: selectedChat.service_id,
                      projectId: selectedChat.project_id,
                    });
                  }}
                  onThreadClick={handleThreadClick}
                />
              ) : null}
            </Box>
            <Box
              sx={{
                flex: isThreadOpen ? `0 0 ${THREAD_WIDTH}px` : "0 0 0px",
                height: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                bgcolor: theme.palette.neutral.main,
                transition: "all .3s ease",
              }}
            >
              {isThreadOpen && (
                <ThreadPanel
                  serviceType={selectedChat.service_type}
                  serviceId={selectedChat.service_id}
                  isDisabled={false}
                  onClose={handleThreadClose}
                />
              )}
            </Box>
          </Stack>
        )}
    </Stack>
  );
};
